import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Link
} from '@mui/material';
import { sumBy } from 'lodash';
import { format } from 'date-fns';
import { AppContext } from '../context/AppProvider';
import { AuthContext } from '../context/AuthProvider';
import { fetchWithInterceptor } from '../utils/fetchWithInterceptor';

const ADMIN_URL = process.env.REACT_APP_SHOPIFY_ADMIN_URL || "";

const getStatusColor = (status) => {
  switch (status) {
    case 'PARTIALLY_FULFILLED':
      return 'warning';
    case 'UNFULFILLED':
      return 'error';
    default:
      return 'default';
  }
};

const ShopifyOrdersList = ({ productSku }) => {  
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const loadOrders = async () => {
    if (isLoading) {
      return;
    }
    if (!productSku) return;
    setIsLoading(true);

    try {
      const params = new URLSearchParams({
        sku: productSku,
      });

      const response = await fetchWithInterceptor(
        `/api/shopify/orders-by-product/?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': authContext.getToken(),
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        appContext.showMessage("Error loading orders");
      }
    } catch (err) {
      appContext.showMessage("Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productSku) {
      loadOrders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSku]);
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (orders.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No orders found for this product
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <Typography variant="h7" sx={{ mb: 2, fontWeight: 600 }}>
          Unfulfilled Orders ({orders.length})
        </Typography>
        <Box sx={{ 
          mb: 3,  
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Total Units:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {(() => {
                const totalUnits = sumBy(orders, (order) => {
                  const orderData = order.node;
                  const targetLineItem = orderData.lineItems?.edges?.find(itemEdge => 
                    itemEdge.node.sku === productSku
                  );
                  return targetLineItem ? targetLineItem.node.quantity : 0;
                });
                return totalUnits;
              })()}
            </Typography>
          </Box>
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 15 }}>Order #</TableCell>
              <TableCell sx={{ fontSize: 15 }}>Order Date</TableCell>
              <TableCell sx={{ fontSize: 15 }}>Customer</TableCell>
              <TableCell sx={{ fontSize: 15 }}>Status</TableCell>
              <TableCell sx={{ fontSize: 15 }}>Quantity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => {
              const orderData = order.node;
              const targetLineItem = orderData.lineItems?.edges?.find(itemEdge => 
                itemEdge.node.sku === productSku
              );
              const skuQuantity = targetLineItem ? targetLineItem.node.quantity : 0;
              
              return (
                <TableRow 
                  key={orderData.id}
                  sx={{ 
                    '&:not(:last-child)': { 
                      mb: 1 
                    },
                    '& td': { 
                      py: 1.5 
                    }
                  }}
                >
                  <TableCell sx={{ fontSize: 18 }}>
                    <Link
                      href={`${ADMIN_URL}/orders/${orderData.id.split('/').pop()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 16 }}>
                        {orderData.name}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell sx={{ fontSize: 16 }}>
                    {format(new Date(orderData.createdAt), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell sx={{ fontSize: 16 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: 16 }}>
                        {orderData.customer?.firstName} {orderData.customer?.lastName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 16 }}>
                    <Chip
                      label={orderData.displayFulfillmentStatus || 'Unknown'}
                      color={getStatusColor(orderData.displayFulfillmentStatus)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 16 }}>
                    <Typography variant="body2" sx={{ fontSize: 16 }}>
                      {skuQuantity}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ShopifyOrdersList;
